'use client';

import { c as _c } from "react/compiler-runtime";
import { MessageCircleQuestion } from 'lucide-react';
import type { Command as CommandDefinition } from './types';
import type { CommandPaletteAiState } from './use-command-palette-ai';
import { CommandItemIcon, CommandItemRenderer, PaletteGroup, PaletteKeys, PaletteRow, PaletteStatusRow } from './command-item-renderer';
import { ASK_AI_COMMAND_ID } from './palette-sections';
type SettledAiState = Exclude<CommandPaletteAiState, {
  status: 'idle' | 'loading';
}>;
function getAiMessage(state: SettledAiState, hasSuggestions: boolean) {
  if (state.status === 'clarify') {
    return state.question || 'I need more information.';
  }
  if (state.status === 'error') {
    return state.message || 'AI search failed.';
  }
  if (state.status === 'none' && state.message) {
    return state.message;
  }
  return hasSuggestions ? null : 'No AI match found.';
}
type CommandPaletteAiSectionProps = {
  alternativeCommands: CommandDefinition[];
  canAskAi: boolean;
  loadingLabel: string;
  onAskAi: () => Promise<void> | void;
  onSelectCommand: (commandId: string) => void;
  searchTerm: string;
  selectedValue: string;
  state: CommandPaletteAiState;
};
export function CommandPaletteAiSection(t0) {
  const $ = _c(23);
  const {
    alternativeCommands,
    canAskAi,
    loadingLabel,
    onAskAi,
    onSelectCommand,
    searchTerm,
    selectedValue,
    state
  } = t0;
  if (!searchTerm) {
    return null;
  }
  if (state.status === "idle") {
    if (!canAskAi) {
      return null;
    }
    let t1;
    if ($[0] !== onAskAi) {
      t1 = () => void onAskAi();
      $[0] = onAskAi;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <CommandItemIcon><MessageCircleQuestion /></CommandItemIcon>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const t3 = `Ask AI: "${searchTerm}"`;
    let t4;
    if ($[3] !== t3) {
      t4 = <span className="min-w-0 flex-1 truncate">{t3}</span>;
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <PaletteKeys keys={["Tab"]} />;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== t1 || $[7] !== t4) {
      t6 = <PaletteGroup><PaletteRow value={ASK_AI_COMMAND_ID} onSelect={t1}>{t2}{t4}{t5}</PaletteRow></PaletteGroup>;
      $[6] = t1;
      $[7] = t4;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  if (state.status === "loading") {
    let t1;
    if ($[9] !== loadingLabel) {
      t1 = <PaletteGroup><PaletteStatusRow>{loadingLabel}</PaletteStatusRow></PaletteGroup>;
      $[9] = loadingLabel;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    return t1;
  }
  const t1 = alternativeCommands.length > 0;
  let t2;
  if ($[11] !== state || $[12] !== t1) {
    t2 = getAiMessage(state, t1);
    $[11] = state;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const message = t2;
  let t3;
  if ($[14] !== message) {
    t3 = message && <PaletteGroup><div className="px-2.5 py-2 text-sm text-muted-foreground">{message}</div></PaletteGroup>;
    $[14] = message;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== alternativeCommands || $[17] !== onSelectCommand || $[18] !== selectedValue) {
    t4 = alternativeCommands.length > 0 && <PaletteGroup heading="Suggestions">{alternativeCommands.map(cmd => <CommandItemRenderer key={cmd.id} command={cmd} selected={cmd.id === selectedValue} onSelect={onSelectCommand} />)}</PaletteGroup>;
    $[16] = alternativeCommands;
    $[17] = onSelectCommand;
    $[18] = selectedValue;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== t3 || $[21] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}